/**
 * MobileNavDrawer — purpose-built off-canvas drawer for SharedNav.
 *
 * Replaces the `@/components/ui/sheet` (Radix Dialog + tailwindcss-
 * animate) that was previously mounted at the mobile hamburger for
 * two reasons documented in the mobile-nav-drawer dispatch:
 *
 *   1. ROOT CAUSE FIX — Radix Dialog's `Sheet` variant uses
 *      tailwindcss-animate's `slide-in-from-right` keyframe animation.
 *      On real iOS Safari the animation could complete without
 *      committing its end-state transform (fill-mode edge cases +
 *      Radix portal remount timing), leaving the panel stranded at
 *      `translateX(≈100%)` — approximately its own width — placing
 *      the entire panel OFF-VIEWPORT to the right. Logo truncated,
 *      close button spilled past the right edge, and 42 descendant
 *      elements reported overflow AS A CONSEQUENCE.
 *
 *      The fix drops the keyframe animation entirely and uses
 *      simple CSS transitions on a controlled `transform` property.
 *      Open state is `translate-x-0`; closed state is `translate-x-
 *      full`. Deterministic, no animation-fill-mode surface area,
 *      no portal re-mount timing to worry about.
 *
 *   2. STRUCTURE — the mobile drawer requires a bespoke body
 *      layout (logo + close in the top row, primary CTAs at the
 *      top of the scroll region, theme toggle at the bottom).
 *      Radix Sheet renders its own absolute-positioned close button
 *      in the top-right corner (fighting our own close-button
 *      placement) and imposes its own padding. A purpose-built
 *      shell is cleaner than fighting the Sheet primitive on both
 *      counts.
 *
 * Other consumers of `@/components/ui/sheet` (Customer nav,
 * ProviderShell, AdminShell, AdminBookingsPage filter drawer) are
 * unaffected — this component is scoped to SharedNav only.
 *
 * Contract:
 *   * Renders into a Portal on document.body so panel transforms
 *     are not clipped by parent overflow/transform contexts.
 *   * `role="dialog"` + `aria-modal="true"` + `aria-label` on the
 *     panel.
 *   * Escape key closes.
 *   * Backdrop click closes.
 *   * Body scroll locked while open (restored on close/unmount).
 *   * Panel: fixed inset-y-0 right-0, `w-[85vw] max-w-sm`, CSS
 *     transition on `transform` over 260ms ease-out.
 *   * Open  → `translate-x-0`
 *     Closed → `translate-x-full`
 */
import React, { useEffect } from "react";
import ReactDOM from "react-dom";

export default function MobileNavDrawer({
  open,
  onOpenChange,
  children,
  ariaLabel = "Site navigation",
}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onOpenChange]);

  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop — separate node so panel translate doesn't fight
          opacity on the same element. `pointer-events-none` when
          closed means clicks fall through to the underlying page. */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-200 ease-out ${
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        style={{ background: "rgba(0, 0, 0, 0.5)" }}
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
        data-testid="mobile-nav-drawer-backdrop"
      />
      {/* Panel — the fix lives here. `translate-x-full` when closed;
          `translate-x-0` when open. NO keyframe animation. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        data-state={open ? "open" : "closed"}
        data-testid="mobile-nav-drawer-panel"
        className={`fixed inset-y-0 right-0 z-50 flex flex-col w-[85vw] max-w-sm transition-transform duration-[260ms] ease-out will-change-transform ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ background: "var(--pp-v3-surface)" }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
